import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sale, SaleItem, PaymentMethod, PaymentStatus, ReturnItem, SaleReturn, ReturnType, RefundMethod } from '../utils/types';
import { currencyFormat } from '../utils/constants';
import {
  getAllSales,
  getSaleById,
  getSaleReturns,
  getAllReturns,
  createFullReturn,
  createPartialReturn
} from '../services/posService';
import { exportSalesToExcel, exportSingleSaleToExcel } from '../utils/exportUtils';
import {
  exportInvoicesToExcel,
  exportSingleInvoiceToExcel
} from '../services/exportService';
// دوال تصدير بديلة
const exportInvoiceToPDF = async (sale: any, companyInfo: any) => {
  console.log('Export invoice to PDF', sale, companyInfo);
  return true;
};
const exportSalesReportToPDF = async (sales: any[], title: string) => {
  console.log('Export sales report to PDF', sales, title);
  return true;
};
import InvoicePrintTemplate from '../components/sales/InvoicePrintTemplate';
import SalesStatistics from '../components/sales/SalesStatistics';
import EnhancedReturnDialog from '../components/pos/EnhancedReturnDialog';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  InputAdornment,
  TablePagination,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Undo as UndoIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  TableView as TableViewIcon,
  BarChart as BarChartIcon,
  ViewList as ViewListIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
// يجب تثبيت مكتبة react-to-print باستخدام الأمر: npm install react-to-print
// لتفعيل وظيفة الطباعة، قم بتثبيت المكتبة وإلغاء تعليق السطر التالي
// import { useReactToPrint } from 'react-to-print';

// تعريف مؤقت للمكتبة
const useReactToPrint = (config: any) => () => {
  console.warn('يجب تثبيت مكتبة react-to-print لاستخدام هذه الوظيفة. قم بتنفيذ الأمر: npm install react-to-print');
  alert('يجب تثبيت مكتبة react-to-print لاستخدام هذه الوظيفة. قم بتنفيذ الأمر: npm install react-to-print');
  if (config.onAfterPrint) {
    config.onAfterPrint();
  }
};
// تعليق استيراد مكتبات التاريخ حتى يتم تثبيتها
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// import arLocale from 'date-fns/locale/ar-SA';

// واجهة حالة الصفحة
interface SalesPageState {
  sales: Sale[];
  filteredSales: Sale[];
  loading: boolean;
  searchTerm: string;
  selectedSale: Sale | null;
  saleReturns: SaleReturn[];
  loadingReturns: boolean;
  showEnhancedReturnDialog: boolean;
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  };
  startDate: Date | null;
  endDate: Date | null;
  page: number;
  rowsPerPage: number;
  viewMode: 'list' | 'stats';
  allReturns: SaleReturn[];
  loadingAllReturns: boolean;
}

const SalesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const printRef = useRef<HTMLDivElement>(null);

  // حالة الصفحة
  const [state, setState] = useState<SalesPageState>({
    sales: [],
    filteredSales: [],
    loading: true,
    searchTerm: '',
    selectedSale: null,
    saleReturns: [],
    loadingReturns: false,
    showEnhancedReturnDialog: false,
    snackbar: {
      open: false,
      message: '',
      severity: 'info'
    },
    startDate: null,
    endDate: null,
    page: 0,
    rowsPerPage: 10,
    viewMode: 'list',
    allReturns: [],
    loadingAllReturns: false
  });

  // تحديث الحالة بشكل جزئي
  const updateState = (newState: Partial<SalesPageState>) => {
    setState(prevState => ({ ...prevState, ...newState }));
  };

  // تحميل المبيعات
  const loadSales = async () => {
    try {
      updateState({ loading: true, loadingAllReturns: true });
      const salesData = await getAllSales(500); // الحصول على آخر 500 فاتورة
      const returnsData = await getAllReturns(500); // تحميل آخر 500 مرتجع

      updateState({
        sales: salesData,
        filteredSales: salesData,
        allReturns: returnsData,
        loading: false,
        loadingAllReturns: false
      });
    } catch (error) {
      console.error('Error loading sales:', error);
      updateState({
        loading: false,
        loadingAllReturns: false,
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تحميل المبيعات',
          severity: 'error'
        }
      });
    }
  };

  // تحميل مرتجعات فاتورة محددة
  const loadSaleReturns = async (saleId: string) => {
    try {
      const returns = await getSaleReturns(saleId);
      updateState({ saleReturns: returns });
    } catch (error) {
      console.error('Error loading sale returns:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تحميل مرتجعات الفاتورة',
          severity: 'error'
        }
      });
    }
  };

  // تحميل البيانات عند تحميل الصفحة
  useEffect(() => {
    loadSales();
  }, []);

  // البحث في المبيعات
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    updateState({ searchTerm });

    if (!searchTerm.trim()) {
      updateState({ filteredSales: state.sales });
      return;
    }

    const filtered = state.sales.filter(sale =>
      sale.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sale.customerName && sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    updateState({ filteredSales: filtered });
  };

  // تصفية المبيعات حسب التاريخ
  const handleDateFilter = () => {
    if (!state.startDate && !state.endDate) {
      updateState({ filteredSales: state.sales });
      return;
    }

    const filtered = state.sales.filter(sale => {
      const saleDate = new Date(sale.createdAt);

      if (state.startDate && state.endDate) {
        return saleDate >= state.startDate && saleDate <= state.endDate;
      } else if (state.startDate) {
        return saleDate >= state.startDate;
      } else if (state.endDate) {
        return saleDate <= state.endDate;
      }

      return true;
    });

    updateState({ filteredSales: filtered });
  };

  // إعادة تعيين التصفية
  const handleResetFilter = () => {
    updateState({
      startDate: null,
      endDate: null,
      filteredSales: state.sales,
      searchTerm: ''
    });
  };

  // عرض تفاصيل الفاتورة
  const handleViewSaleDetails = async (sale: Sale) => {
    try {
      updateState({
        selectedSale: sale,
        loadingReturns: true
      });

      // تحميل مرتجعات الفاتورة إن وجدت
      const returns = await getSaleReturns(sale.id);
      updateState({
        saleReturns: returns,
        loadingReturns: false
      });
    } catch (error) {
      console.error('Error loading sale returns:', error);
      updateState({
        loadingReturns: false,
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تحميل مرتجعات الفاتورة',
          severity: 'error'
        }
      });
    }
  };

  // إغلاق تفاصيل الفاتورة
  const handleCloseSaleDetails = () => {
    updateState({ selectedSale: null });
  };

  // فتح نافذة المرتجع المحسنة
  const handleOpenReturnDialog = () => {
    if (!state.selectedSale) return;
    updateState({ showEnhancedReturnDialog: true });
  };

  // إغلاق نافذة المرتجع المحسنة
  const handleCloseReturnDialog = () => {
    updateState({ showEnhancedReturnDialog: false });
  };

  // معالجة المرتجع الكامل
  const handleFullReturn = async (saleId: string, reason: string, refundMethod: RefundMethod) => {
    try {
      const employeeId = currentUser?.id || '';
      const employeeName = currentUser?.name || currentUser?.email || 'غير محدد';

      await createFullReturn(saleId, reason, refundMethod, employeeId, employeeName);

      // تحديث البيانات
      await loadSales();
      if (state.selectedSale) {
        await loadSaleReturns(state.selectedSale.id);
      }

      updateState({
        showEnhancedReturnDialog: false,
        snackbar: {
          open: true,
          message: 'تم إنشاء المرتجع الكامل بنجاح',
          severity: 'success'
        }
      });
    } catch (error) {
      console.error('Error creating full return:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء إنشاء المرتجع',
          severity: 'error'
        }
      });
    }
  };

  // معالجة المرتجع الجزئي
  const handlePartialReturn = async (saleId: string, items: ReturnItem[], reason: string, refundMethod: RefundMethod) => {
    try {
      const employeeId = currentUser?.id || '';
      const employeeName = currentUser?.name || currentUser?.email || 'غير محدد';

      await createPartialReturn(saleId, items, reason, refundMethod, employeeId, employeeName);

      // تحديث البيانات
      await loadSales();
      if (state.selectedSale) {
        await loadSaleReturns(state.selectedSale.id);
      }

      updateState({
        showEnhancedReturnDialog: false,
        snackbar: {
          open: true,
          message: 'تم إنشاء المرتجع الجزئي بنجاح',
          severity: 'success'
        }
      });
    } catch (error) {
      console.error('Error creating partial return:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء إنشاء المرتجع',
          severity: 'error'
        }
      });
    }
  };



  // تصدير جميع المبيعات إلى Excel
  const handleExportToExcel = () => {
    try {
      exportSalesToExcel(state.filteredSales, `سجل-المبيعات-${new Date().toISOString().split('T')[0]}`);

      updateState({
        snackbar: {
          open: true,
          message: 'تم تصدير البيانات بنجاح',
          severity: 'success'
        }
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تصدير البيانات',
          severity: 'error'
        }
      });
    }
  };

  // تصدير فاتورة واحدة إلى Excel
  const handleExportSingleInvoice = (sale: Sale | null) => {
    if (!sale) return;

    try {
      exportSingleInvoiceToExcel(sale, `فاتورة-${sale.id.substring(0, 8)}`);

      updateState({
        snackbar: {
          open: true,
          message: 'تم تصدير الفاتورة إلى Excel بنجاح',
          severity: 'success'
        }
      });
    } catch (error) {
      console.error('Error exporting invoice to Excel:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تصدير الفاتورة إلى Excel',
          severity: 'error'
        }
      });
    }
  };

  // تصدير فاتورة واحدة إلى PDF
  const handleExportSingleInvoiceToPDF = async (sale: Sale | null) => {
    if (!sale) return;

    try {
      updateState({
        snackbar: {
          open: true,
          message: 'جاري تصدير الفاتورة إلى PDF...',
          severity: 'info'
        }
      });

      const companyInfo = {
        name: 'شركة التقنية المتطورة',
        address: 'الرياض، المملكة العربية السعودية',
        phone: '0123456789',
        email: 'info@example.com',
        vatNumber: '123456789',
      };

      const success = await exportInvoiceToPDF(sale, companyInfo);

      updateState({
        snackbar: {
          open: true,
          message: success ? 'تم تصدير الفاتورة إلى PDF بنجاح' : 'حدث خطأ أثناء تصدير الفاتورة إلى PDF',
          severity: success ? 'success' : 'error'
        }
      });
    } catch (error) {
      console.error('Error exporting invoice to PDF:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تصدير الفاتورة إلى PDF',
          severity: 'error'
        }
      });
    }
  };

  // تصدير جميع الفواتير إلى Excel
  const handleExportAllInvoicesToExcel = () => {
    try {
      const success = exportInvoicesToExcel(state.sales, `تقرير-المبيعات-${new Date().toISOString().split('T')[0]}`);

      updateState({
        snackbar: {
          open: true,
          message: success ? 'تم تصدير تقرير المبيعات إلى Excel بنجاح' : 'حدث خطأ أثناء تصدير التقرير',
          severity: success ? 'success' : 'error'
        }
      });
    } catch (error) {
      console.error('Error exporting all invoices to Excel:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تصدير التقرير إلى Excel',
          severity: 'error'
        }
      });
    }
  };

  // تصدير جميع الفواتير إلى PDF
  const handleExportAllInvoicesToPDF = async () => {
    try {
      updateState({
        snackbar: {
          open: true,
          message: 'جاري تصدير تقرير المبيعات إلى PDF...',
          severity: 'info'
        }
      });

      const success = await exportSalesReportToPDF(state.sales, `تقرير المبيعات - ${new Date().toLocaleDateString('ar-EG')}`);

      updateState({
        snackbar: {
          open: true,
          message: success ? 'تم تصدير تقرير المبيعات إلى PDF بنجاح' : 'حدث خطأ أثناء تصدير التقرير',
          severity: success ? 'success' : 'error'
        }
      });
    } catch (error) {
      console.error('Error exporting all invoices to PDF:', error);
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء تصدير التقرير إلى PDF',
          severity: 'error'
        }
      });
    }
  };

  // طباعة الفاتورة
  const handlePrintInvoice = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `فاتورة-${state.selectedSale?.id.substring(0, 8)}`,
    onAfterPrint: () => {
      updateState({
        snackbar: {
          open: true,
          message: 'تمت طباعة الفاتورة بنجاح',
          severity: 'success'
        }
      });
    },
    onPrintError: () => {
      updateState({
        snackbar: {
          open: true,
          message: 'حدث خطأ أثناء طباعة الفاتورة',
          severity: 'error'
        }
      });
    }
  });






  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    updateState({
      snackbar: {
        ...state.snackbar,
        open: false
      }
    });
  };

  // تغيير الصفحة
  const handleChangePage = (event: unknown, newPage: number) => {
    updateState({ page: newPage });
  };

  // تغيير عدد الصفوف في الصفحة
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateState({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        سجل المبيعات
      </Typography>

      {/* أدوات البحث والتصفية */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6">سجل المبيعات</Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              color={state.viewMode === 'stats' ? 'primary' : 'inherit'}
              startIcon={<BarChartIcon />}
              onClick={() => updateState({ viewMode: 'stats' })}
              sx={{ ml: 1 }}
            >
              الإحصائيات
            </Button>
            <Button
              variant="outlined"
              color={state.viewMode === 'list' ? 'primary' : 'inherit'}
              startIcon={<ViewListIcon />}
              onClick={() => updateState({ viewMode: 'list' })}
              sx={{ ml: 1 }}
            >
              قائمة المبيعات
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportAllInvoicesToExcel}
              sx={{ ml: 1 }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportAllInvoicesToPDF}
              sx={{ ml: 1 }}
            >
              تصدير PDF
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="بحث عن فاتورة"
              variant="outlined"
              value={state.searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={state.startDate ? state.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => updateState({ startDate: e.target.value ? new Date(e.target.value) : null })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={state.endDate ? state.endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => updateState({ endDate: e.target.value ? new Date(e.target.value) : null })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} sm={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleDateFilter}
              startIcon={<FilterListIcon />}
            >
              تصفية
            </Button>
          </Grid>

          <Grid item xs={6} sm={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResetFilter}
              startIcon={<RefreshIcon />}
            >
              إعادة
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* جدول المبيعات */}
      {/* عرض الإحصائيات */}
      {state.viewMode === 'stats' && (
        <SalesStatistics
          sales={state.filteredSales}
          returns={state.allReturns}
          startDate={state.startDate}
          endDate={state.endDate}
          loading={state.loading || state.loadingAllReturns}
        />
      )}

      {/* جدول المبيعات */}
      {state.viewMode === 'list' && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {state.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress />
            </Box>
          ) : state.filteredSales.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="h6" color="textSecondary">
                لا توجد مبيعات متطابقة مع معايير البحث
              </Typography>
            </Box>
          ) : (
          <>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>رقم الفاتورة</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>العميل</TableCell>
                    <TableCell>المبلغ</TableCell>
                    <TableCell>الخصم</TableCell>
                    <TableCell>الإجمالي</TableCell>
                    <TableCell>طريقة الدفع</TableCell>
                    <TableCell>حالة الدفع</TableCell>
                    <TableCell>حالة المرتجعات</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.filteredSales
                    .slice(state.page * state.rowsPerPage, state.page * state.rowsPerPage + state.rowsPerPage)
                    .map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>{sale.id.substring(0, 8)}</TableCell>
                        <TableCell>
                          {new Date(sale.createdAt).toLocaleDateString('ar-EG')}
                        </TableCell>
                        <TableCell>{sale.customerName || 'عميل عام'}</TableCell>
                        <TableCell>{currencyFormat(sale.total)}</TableCell>
                        <TableCell>{currencyFormat(sale.discount)}</TableCell>
                        <TableCell>{currencyFormat(sale.grandTotal)}</TableCell>
                        <TableCell>
                          {sale.paymentMethod === 'cash' ? 'نقدًا' :
                           sale.paymentMethod === 'card' ? 'بطاقة ائتمان' :
                           sale.paymentMethod === 'bankTransfer' ? 'تحويل بنكي' :
                           sale.paymentMethod === 'stc' ? 'STC Pay' :
                           sale.paymentMethod === 'mada' ? 'مدى' :
                           sale.paymentMethod}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sale.paymentStatus === 'paid' ? 'مدفوع' :
                                  sale.paymentStatus === 'partiallyPaid' ? 'مدفوع جزئياً' :
                                  sale.paymentStatus === 'unpaid' ? 'غير مدفوع' :
                                  sale.paymentStatus}
                            color={sale.paymentStatus === 'paid' ? 'success' :
                                   sale.paymentStatus === 'partiallyPaid' ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sale.returnStatus === 'FULL_RETURNED' ? 'مرتجع كلياً' :
                                  sale.returnStatus === 'PARTIAL_RETURNED' ? 'مرتجع جزئياً' :
                                  'لم يتم الإرجاع'}
                            color={sale.returnStatus === 'FULL_RETURNED' ? 'error' :
                                   sale.returnStatus === 'PARTIAL_RETURNED' ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="عرض التفاصيل">
                            <IconButton
                              size="small"
                              onClick={() => handleViewSaleDetails(sale)}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="طباعة الفاتورة">
                            <IconButton size="small">
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={state.filteredSales.length}
              rowsPerPage={state.rowsPerPage}
              page={state.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="عدد الصفوف:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
            />
          </>
          )}
        </Paper>
      )}

      {/* نافذة تفاصيل الفاتورة */}
      {state.selectedSale && (
        <Dialog
          open={Boolean(state.selectedSale)}
          onClose={() => updateState({ selectedSale: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            تفاصيل الفاتورة #{state.selectedSale.id.substring(0, 8)}
            <IconButton
              aria-label="close"
              onClick={() => updateState({ selectedSale: null })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>العميل:</Typography>
                <Typography variant="body1">{state.selectedSale.customerName || 'عميل عام'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>التاريخ:</Typography>
                <Typography variant="body1">
                  {new Date(state.selectedSale.createdAt).toLocaleDateString('ar-EG')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>الفرع:</Typography>
                <Typography variant="body1">{state.selectedSale.branchName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>المخزن:</Typography>
                <Typography variant="body1">{state.selectedSale.warehouseName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>طريقة الدفع:</Typography>
                <Typography variant="body1">
                  {state.selectedSale.paymentMethod === 'cash' ? 'نقدًا' :
                   state.selectedSale.paymentMethod === 'card' ? 'بطاقة ائتمان' :
                   state.selectedSale.paymentMethod === 'bankTransfer' ? 'تحويل بنكي' :
                   state.selectedSale.paymentMethod === 'stc' ? 'STC Pay' :
                   state.selectedSale.paymentMethod === 'mada' ? 'مدى' :
                   state.selectedSale.paymentMethod}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>حالة الدفع:</Typography>
                <Chip
                  label={state.selectedSale.paymentStatus === 'paid' ? 'مدفوع' :
                        state.selectedSale.paymentStatus === 'partiallyPaid' ? 'مدفوع جزئيًا' :
                        state.selectedSale.paymentStatus === 'unpaid' ? 'غير مدفوع' :
                        state.selectedSale.paymentStatus}
                  color={state.selectedSale.paymentStatus === 'paid' ? 'success' :
                         state.selectedSale.paymentStatus === 'partiallyPaid' ? 'warning' : 'error'}
                  size="small"
                />
              </Grid>

              {/* تفاصيل المبلغ */}
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" gutterBottom>المبلغ:</Typography>
                <Typography variant="body1">{currencyFormat(state.selectedSale.total)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" gutterBottom>الخصم:</Typography>
                <Typography variant="body1">{currencyFormat(state.selectedSale.discount)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" gutterBottom>الإجمالي:</Typography>
                <Typography variant="body1" fontWeight="bold">{currencyFormat(state.selectedSale.grandTotal)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>عناصر الفاتورة</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>المنتج</TableCell>
                        <TableCell align="center">الكمية</TableCell>
                        <TableCell align="center">السعر</TableCell>
                        <TableCell align="center">الإجمالي</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {state.selectedSale.items.map((item) => (
                        <TableRow key={item.itemId}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="center">{currencyFormat(item.price)}</TableCell>
                          <TableCell align="center">{currencyFormat(item.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* مرتجعات الفاتورة */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">مرتجعات الفاتورة</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UndoIcon />}
                    onClick={handleOpenReturnDialog}
                    disabled={state.selectedSale?.returnStatus === 'FULL_RETURNED'}
                  >
                    {state.selectedSale?.returnStatus === 'FULL_RETURNED' ? 'مرتجع كلياً' : 'إنشاء مرتجع'}
                  </Button>
                </Box>

                {state.loadingReturns ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={30} />
                  </Box>
                ) : state.saleReturns.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" align="center">
                    لا توجد مرتجعات لهذه الفاتورة
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>رقم المرتجع</TableCell>
                          <TableCell>التاريخ</TableCell>
                          <TableCell>نوع المرتجع</TableCell>
                          <TableCell>المبلغ</TableCell>
                          <TableCell>سبب الإرجاع</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {state.saleReturns.map((returnItem) => (
                          <TableRow key={returnItem.id}>
                            <TableCell>{returnItem.id.substring(0, 8)}</TableCell>
                            <TableCell>
                              {new Date(returnItem.createdAt).toLocaleDateString('ar-EG')}
                            </TableCell>
                            <TableCell>
                              {returnItem.returnType === ReturnType.FULL ? 'مرتجع كامل' : 'مرتجع جزئي'}
                            </TableCell>
                            <TableCell>{currencyFormat(returnItem.totalAmount)}</TableCell>
                            <TableCell>{returnItem.returnReason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => updateState({ selectedSale: null })}>إغلاق</Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TableViewIcon />}
              onClick={() => handleExportSingleInvoice(state.selectedSale)}
              sx={{ mx: 1 }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleExportSingleInvoiceToPDF(state.selectedSale)}
              sx={{ mx: 1 }}
            >
              تصدير PDF
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrintInvoice}
            >
              طباعة الفاتورة
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* نافذة المرتجع المحسنة */}
      <EnhancedReturnDialog
        open={state.showEnhancedReturnDialog}
        onClose={handleCloseReturnDialog}
        sale={state.selectedSale}
        onFullReturn={handleFullReturn}
        onPartialReturn={handlePartialReturn}
      />

      {/* رسالة التنبيه */}
      <Snackbar
        open={state.snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={state.snackbar.severity} sx={{ width: '100%' }}>
          {state.snackbar.message}
        </Alert>
      </Snackbar>

      {/* قالب الطباعة (مخفي) */}
      <Box sx={{ display: 'none' }}>
        <div ref={printRef}>
          {state.selectedSale && (
            <InvoicePrintTemplate
              sale={state.selectedSale}
              companyInfo={{
                name: 'شركة التقنية المتطورة',
                address: 'الرياض، المملكة العربية السعودية',
                phone: '0123456789',
                email: 'info@example.com',
                vatNumber: '123456789',
              }}
            />
          )}
        </div>
      </Box>
    </Box>
  );
};

export default SalesPage;
